import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import {
  Users,
  BookOpen,
  RotateCw,
  MapPin,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
  Clock,
  RefreshCw,
  CalendarClock,
  Replace,
} from "lucide-react";

// Sprint C (Feb 2026) – Anti-Doppelzuweisungs-Action-Codes + UI-Mapping.
// Sortierung definiert die Reihenfolge im Ergebnis-Modal (positive zuerst).
const ACTION_DISPLAY = [
  {
    key: "ASSIGNMENT_CREATED",
    label: "Neu zugewiesen",
    icon: CheckCircle2,
    tone: "success",
  },
  {
    key: "ASSIGNMENT_CREATED_NEW_VERSION",
    label: "Neue Version – neu zugewiesen",
    icon: RefreshCw,
    tone: "info",
  },
  {
    key: "ASSIGNMENT_CREATED_INTERVAL_EXPIRED",
    label: "Intervall abgelaufen – neu zugewiesen",
    icon: CalendarClock,
    tone: "info",
  },
  {
    key: "ASSIGNMENT_REPLACED_BY_NEW_VERSION",
    label: "Alte Aufgabe durch neue Version ersetzt",
    icon: Replace,
    tone: "warning",
  },
  {
    key: "ASSIGNMENT_SKIPPED_ALREADY_EXISTS",
    label: "Übersprungen – bereits offen vorhanden",
    icon: Clock,
    tone: "muted",
  },
  {
    key: "ASSIGNMENT_SKIPPED_VALID_PROOF",
    label: "Übersprungen – gültiger Nachweis vorhanden",
    icon: ShieldCheck,
    tone: "muted",
  },
];

const TONE_CLASSES = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-900",
  info: "bg-sky-50 border-sky-200 text-sky-900",
  warning: "bg-amber-50 border-amber-200 text-amber-900",
  muted: "bg-slate-50 border-slate-200 text-slate-700",
};

const TONE_ICON_CLASSES = {
  success: "text-emerald-700",
  info: "text-sky-700",
  warning: "text-amber-700",
  muted: "text-slate-500",
};

// Die vier zulässigen Wiedervorlage-Intervalle (synchron mit Backend-Enum).
const RECURRENCE_OPTIONS = [
  { value: "einmalig", label: "Einmalig" },
  { value: "monatlich", label: "Monatlich" },
  { value: "halbjaehrlich", label: "Alle 6 Monate" },
  { value: "jaehrlich", label: "Jährlich" },
];

/**
 * AssignDialog
 *
 * Phase 3 (Modul 1 — Welt-Verteilung):
 * Wenn die Welt einsatzbereich_ids gesetzt hat, werden die passenden aktiven
 * Mitarbeiter (Schnittmenge ihrer einsatzbereich_ids mit denen der Welt)
 * automatisch vorausgewählt. Oben erscheint ein Hinweis-Banner. Der
 * Unternehmer kann die Auswahl jederzeit verändern.
 *
 * Sprint C (Feb 2026) – Enterprise-Doppelzuweisungs-Schutz:
 * Nach dem Submit zeigt der Dialog ein Ergebnis-Panel mit der Aufschlüsselung
 * der Server-Entscheidungen (CREATED / SKIPPED / REPLACED). Der Unternehmer
 * muss nie wieder selbst nachdenken, ob etwas schon zugewiesen wurde.
 */
export default function AssignDialog({
  open,
  onOpenChange,
  onAssigned,
  courseId,
  courseTitle,
  courseRecurrence,
  courseRecurrenceReason,
  courseEinsatzbereichIds = [],
}) {
  const [employees, setEmployees] = useState([]);
  const [einsatzbereiche, setEinsatzbereiche] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dueInDays, setDueInDays] = useState(14);
  const [recurrence, setRecurrence] = useState(courseRecurrence || "jaehrlich");
  const [submitting, setSubmitting] = useState(false);
  // Sprint C: Ergebnis-Modus nach erfolgreichem Submit.
  const [result, setResult] = useState(null);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  // Welche Welt-Einsatzbereiche (Namen) sollen im Banner gezeigt werden?
  const ebNameById = useMemo(() => {
    const m = new Map();
    for (const e of einsatzbereiche) m.set(e.id, e.name);
    return m;
  }, [einsatzbereiche]);

  const courseEbNames = useMemo(
    () =>
      (courseEinsatzbereichIds || [])
        .map((id) => ebNameById.get(id))
        .filter(Boolean),
    [courseEinsatzbereichIds, ebNameById]
  );

  // Mitarbeiter, deren einsatzbereich_ids mit denen der Welt überlappen.
  const matchingIds = useMemo(() => {
    if (!courseEinsatzbereichIds || courseEinsatzbereichIds.length === 0) {
      return [];
    }
    const ebSet = new Set(courseEinsatzbereichIds);
    return employees
      .filter((e) => (e.einsatzbereich_ids || []).some((id) => ebSet.has(id)))
      .map((e) => e.id);
  }, [employees, courseEinsatzbereichIds]);

  // Dialog wird beim Öffnen frisch geladen. Vorauswahl & Reset passieren hier,
  // damit beim erneuten Öffnen mit anderem Kurs der State sauber neu aufgebaut wird.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setResult(null);
      const [emps, ebs] = await Promise.all([
        api.listEmployees(),
        api.listEinsatzbereiche(true),
      ]);
      if (cancelled) return;
      setEmployees(emps);
      setEinsatzbereiche(ebs);
      setDueInDays(14);
      setRecurrence(courseRecurrence || "jaehrlich");

      // Vorauswahl nur, wenn die Welt überhaupt Einsatzbereiche hat
      // UND es passende Mitarbeiter gibt – sonst leer starten.
      if (courseEinsatzbereichIds && courseEinsatzbereichIds.length > 0) {
        const ebSet = new Set(courseEinsatzbereichIds);
        const preselected = emps
          .filter((e) =>
            (e.einsatzbereich_ids || []).some((id) => ebSet.has(id))
          )
          .map((e) => e.id);
        setSelectedIds(new Set(preselected));
      } else {
        setSelectedIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, courseId, courseRecurrence]);

  // Quick-Picks: „Alle aktiven" + ein Pick pro Einsatzbereich, in dem
  // mindestens ein aktiver Mitarbeiter steht. Die Buttons sind ein allgemeines
  // Filter-Werkzeug für den Unternehmer und unabhängig davon, welche
  // einsatzbereich_ids die KI dieser konkreten Welt zugeordnet hat. Bei
  // abstrakten Themen (z. B. „Allgemeine Arbeitssicherheit") gibt die KI
  // bewusst eine leere Liste zurück – die Schnellauswahl bleibt trotzdem voll
  // bedienbar (Phase 6.1-Fix, Feb 2026).
  const quickPicks = useMemo(() => {
    const picks = [
      {
        key: "all",
        label: "Alle aktiven Mitarbeiter",
        ids: employees.map((e) => e.id),
        icon: Users,
        isWeltMatch: false,
      },
    ];

    // Map: einsatzbereich_id → IDs aller aktiven Mitarbeiter in diesem Bereich.
    // Wir sortieren die Bereiche nach Größe (meiste Mitarbeiter zuerst),
    // damit die wichtigsten Picks links stehen.
    const courseEbSet = new Set(courseEinsatzbereichIds || []);
    const ebStats = einsatzbereiche
      .filter((eb) => !eb.archived)
      .map((eb) => {
        const ids = employees
          .filter((e) => (e.einsatzbereich_ids || []).includes(eb.id))
          .map((e) => e.id);
        return { eb, ids };
      })
      .filter((x) => x.ids.length > 0)
      .sort((a, b) => b.ids.length - a.ids.length);

    for (const { eb, ids } of ebStats) {
      picks.push({
        key: `eb-${eb.id}`,
        label: `Alle in „${eb.name}"`,
        ids,
        icon: MapPin,
        // Hervorheben, wenn dieser Bereich von der KI zur Welt passt.
        isWeltMatch: courseEbSet.has(eb.id),
      });
    }
    return picks;
  }, [employees, einsatzbereiche, courseEinsatzbereichIds]);

  const applyQuickPick = (ids) => {
    setSelectedIds(new Set(ids));
  };

  const isQuickPickActive = (ids) => {
    if (ids.length === 0 || ids.length !== selectedIds.size) return false;
    return ids.every((id) => selectedIds.has(id));
  };

  const toggleEmployee = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!courseId || selectedIds.size === 0) return;
    setSubmitting(true);
    clearPolicyError();
    try {
      const res = await api.createAssignment({
        employee_ids: Array.from(selectedIds),
        course_id: courseId,
        due_in_days: dueInDays,
        recurrence,
      });
      // Sprint C: Ergebnis-Panel anzeigen statt Dialog zu schließen.
      setResult(res);
      onAssigned?.(res);
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Zuweisung fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeAfterResult = () => {
    setResult(null);
    onOpenChange(false);
  };

  const selectedCount = selectedIds.size;
  const hasCourseEbs = courseEbNames.length > 0;

  // Sprint C – Ergebnis-Aggregation für die Anzeige (nur Kategorien mit count > 0).
  const resultCategories = useMemo(() => {
    if (!result?.summary) return [];
    return ACTION_DISPLAY.map((d) => ({
      ...d,
      count: result.summary[d.key] || 0,
      decisions: (result.decisions || []).filter((x) => x.action === d.key),
    })).filter((c) => c.count > 0);
  }, [result]);

  const totalCreated = useMemo(() => {
    if (!result?.summary) return 0;
    return (
      (result.summary.ASSIGNMENT_CREATED || 0) +
      (result.summary.ASSIGNMENT_CREATED_NEW_VERSION || 0) +
      (result.summary.ASSIGNMENT_CREATED_INTERVAL_EXPIRED || 0) +
      (result.summary.ASSIGNMENT_REPLACED_BY_NEW_VERSION || 0)
    );
  }, [result]);

  const totalSkipped = useMemo(() => {
    if (!result?.summary) return 0;
    return (
      (result.summary.ASSIGNMENT_SKIPPED_ALREADY_EXISTS || 0) +
      (result.summary.ASSIGNMENT_SKIPPED_VALID_PROOF || 0)
    );
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={result ? () => {} : onOpenChange}>
      <DialogContent
        className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="assign-dialog"
      >
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle
                className="font-heading text-xl"
                data-testid="assign-result-title"
              >
                Zuweisung abgeschlossen
              </DialogTitle>
              <DialogDescription>
                Das System hat für jeden Mitarbeiter geprüft, ob diese Welt
                bereits offen oder aktuell gültig dokumentiert war.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Top-Summe */}
              <div
                className="grid grid-cols-2 gap-3"
                data-testid="assign-result-summary"
              >
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-medium">
                    Neu erstellt / aktualisiert
                  </div>
                  <div
                    className="font-heading text-3xl font-bold text-emerald-900 mt-1"
                    data-testid="assign-result-created-count"
                  >
                    {totalCreated}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-600 font-medium">
                    Übersprungen
                  </div>
                  <div
                    className="font-heading text-3xl font-bold text-slate-900 mt-1"
                    data-testid="assign-result-skipped-count"
                  >
                    {totalSkipped}
                  </div>
                </div>
              </div>

              {/* Kategorien-Aufschlüsselung */}
              <div className="space-y-2">
                {resultCategories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <div
                      key={cat.key}
                      className={`rounded-md border px-4 py-3 ${TONE_CLASSES[cat.tone]}`}
                      data-testid={`assign-result-${cat.key.toLowerCase()}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon
                          className={`w-5 h-5 mt-0.5 shrink-0 ${TONE_ICON_CLASSES[cat.tone]}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="font-medium text-sm">
                              {cat.label}
                            </span>
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 text-[10px] bg-white/70 border-white"
                              data-testid={`assign-result-${cat.key.toLowerCase()}-count`}
                            >
                              {cat.count}
                            </Badge>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {cat.decisions.map((d) => (
                              <span
                                key={`${d.employee_id}-${cat.key}`}
                                className="inline-block text-xs px-2 py-0.5 rounded bg-white/70 border border-white"
                                title={d.reason}
                              >
                                {d.employee_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalCreated === 0 && totalSkipped > 0 && (
                <p className="text-sm text-slate-600 italic">
                  Keine neue Aufgabe wurde erstellt – alle ausgewählten
                  Mitarbeiter sind bereits sauber dokumentiert.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={closeAfterResult}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="assign-result-close"
              >
                Schließen
              </Button>
            </DialogFooter>
          </>
        ) : (
          <></>
        )}
        {!result && (
          <>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            An wen zuweisen?
          </DialogTitle>
          <DialogDescription>
            Empfänger und Frist wählen – das System verteilt die Aufgaben automatisch.
          </DialogDescription>
        </DialogHeader>

        {policyError && (
          <AccountPolicyBanner
            error={policyError}
            onDismiss={clearPolicyError}
            className="mt-2"
          />
        )}

        <div className="space-y-5 py-2">
          {/* Unterweisung – read-only, kommt aus der Bibliothek (ADR-000 v1.3, Prinzip 14) */}
          <div
            className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3"
            data-testid="assign-course-readonly"
          >
            <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">
                Unterweisung
              </div>
              <div
                className="text-sm font-medium text-slate-900 truncate"
                data-testid="assign-course-title"
              >
                {courseTitle || "—"}
              </div>
            </div>
          </div>

          {/* Welt-Verteilung Banner (Phase 3) */}
          {hasCourseEbs && (
            <div
              className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3"
              data-testid="assign-einsatzbereich-banner"
            >
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-indigo-700 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-indigo-900">
                    Diese Welt betrifft{" "}
                    {courseEbNames.length === 1
                      ? `den Einsatzbereich „${courseEbNames[0]}"`
                      : `die Einsatzbereiche ${courseEbNames.map((n) => `„${n}"`).join(", ")}`}
                    .
                  </div>
                  <div className="text-xs text-indigo-800 mt-0.5">
                    {matchingIds.length === 0 ? (
                      <>
                        Aktuell ist niemand aus diesen Bereichen aktiv. Sie
                        können die Empfänger trotzdem manuell wählen.
                      </>
                    ) : matchingIds.length === 1 ? (
                      <>1 Mitarbeiter ist vorausgewählt.</>
                    ) : (
                      <>
                        {matchingIds.length} Mitarbeiter sind vorausgewählt – Sie
                        können die Auswahl jederzeit anpassen.
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empfänger */}
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <Label className="text-sm font-medium">Empfänger</Label>
              <span
                className="text-xs text-slate-500"
                data-testid="assign-selected-count"
              >
                {selectedCount === 0
                  ? "Niemand ausgewählt"
                  : selectedCount === 1
                    ? "1 Mitarbeiter ausgewählt"
                    : `${selectedCount} Mitarbeiter ausgewählt`}
              </span>
            </div>

            {/* Quick-Picks */}
            <div className="flex flex-wrap gap-2">
              {quickPicks.map((qp) => {
                const active = isQuickPickActive(qp.ids);
                const Icon = qp.icon;
                return (
                  <button
                    key={qp.key}
                    onClick={() => applyQuickPick(qp.ids)}
                    data-testid={`assign-quickpick-${qp.key}`}
                    title={
                      qp.isWeltMatch
                        ? "Von der KI dieser Welt zugeordnet"
                        : undefined
                    }
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : qp.isWeltMatch
                          ? "bg-indigo-50 text-indigo-900 border-indigo-300 hover:border-indigo-400"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {qp.isWeltMatch && !active && (
                      <Sparkles className="w-3 h-3" />
                    )}
                    {!qp.isWeltMatch && <Icon className="w-3.5 h-3.5" />}
                    {active && <Icon className="w-3.5 h-3.5" />}
                    {qp.label}
                    <Badge
                      variant="outline"
                      className={`ml-1 h-5 px-1.5 text-[10px] ${
                        active
                          ? "bg-white/10 border-white/30 text-white"
                          : qp.isWeltMatch
                            ? "bg-white border-indigo-200 text-indigo-700"
                            : "bg-slate-100 border-slate-200 text-slate-600"
                      }`}
                    >
                      {qp.ids.length}
                    </Badge>
                  </button>
                );
              })}
            </div>

            {/* Einzel-Auswahl */}
            <div
              className="border border-slate-200 rounded-md divide-y divide-slate-100"
              data-testid="assign-employee-list"
            >
              {employees.map((e) => {
                const ebs = e.einsatzbereiche || [];
                return (
                  <label
                    key={e.id}
                    htmlFor={`emp-${e.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      id={`emp-${e.id}`}
                      checked={selectedIds.has(e.id)}
                      onCheckedChange={() => toggleEmployee(e.id)}
                      data-testid={`assign-employee-checkbox-${e.id}`}
                    />
                    {e.photo_url ? (
                      <img
                        src={e.photo_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover bg-slate-100"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {e.first_name} {e.last_name}
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {ebs.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">
                            kein Einsatzbereich
                          </span>
                        ) : (
                          ebs.map((eb) => (
                            <span
                              key={eb.id}
                              className="text-[11px] text-slate-500 inline-flex items-center"
                            >
                              <MapPin className="w-3 h-3 mr-0.5 text-slate-400" />
                              {eb.name}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Frist */}
          <div className="space-y-2">
            <Label htmlFor="due" className="text-sm font-medium">
              Frist (in Tagen)
            </Label>
            <Input
              id="due"
              type="number"
              min={1}
              max={365}
              value={dueInDays}
              onChange={(e) =>
                setDueInDays(Math.max(1, parseInt(e.target.value || "1", 10)))
              }
              data-testid="assign-due-input"
              className="h-11 max-w-[120px]"
            />
            <p className="text-xs text-slate-500">
              Gleiche Frist gilt für alle Empfänger.
            </p>
          </div>

          {/* Wiedervorlage */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <RotateCw className="w-4 h-4 text-slate-500" />
              <Label className="text-sm font-medium">Wiedervorlage</Label>
            </div>
            <div
              className="flex flex-wrap gap-2"
              data-testid="assign-recurrence-pills"
            >
              {RECURRENCE_OPTIONS.map((opt) => {
                const active = recurrence === opt.value;
                const isAiSuggested =
                  courseRecurrence && opt.value === courseRecurrence;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRecurrence(opt.value)}
                    data-testid={`assign-recurrence-${opt.value}`}
                    className={`relative inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {opt.label}
                    {isAiSuggested && !active && (
                      <span
                        className="ml-1.5 text-[9px] uppercase tracking-wider text-emerald-600 font-semibold"
                        data-testid="assign-recurrence-ai-hint"
                      >
                        KI-Vorschlag
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {courseRecurrenceReason && (
              <p
                className="text-xs text-slate-500 italic"
                data-testid="assign-recurrence-reason"
              >
                KI-Begründung: {courseRecurrenceReason}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="assign-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={!courseId || selectedCount === 0 || submitting}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="assign-confirm"
          >
            {submitting
              ? "Wird zugewiesen…"
              : selectedCount === 0
                ? "Empfänger wählen"
                : selectedCount === 1
                  ? "An 1 Mitarbeiter zuweisen"
                  : `An ${selectedCount} Mitarbeiter zuweisen`}
          </Button>
        </DialogFooter>

        {courseTitle && selectedCount > 0 && (
          <p className="text-xs text-slate-500 -mt-2 pt-2 border-t border-slate-100">
            {"\u201E"}
            {courseTitle}
            {"\u201C"} wird an {selectedCount} Mitarbeiter zugewiesen, Frist{" "}
            {dueInDays} Tage.{" "}
            {recurrence === "einmalig"
              ? "Keine Wiedervorlage."
              : `Wiedervorlage: ${RECURRENCE_OPTIONS.find((o) => o.value === recurrence)?.label}.`}
          </p>
        )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
